/**
 * Badge — small neutral chip primitive for labels, counts, and status pills.
 *
 * Tone presets:
 * - "neutral" (default): slate background, slate text — used for counts and labels
 * - "success": emerald — used for win/done states
 * - "danger": rose — used for lose/error states
 * - "info": sky — used for in-progress/announcement
 * - "warning": amber — used for archived/paused states
 */
import * as React from "react";

import { cn } from "@/lib/utils";

export type BadgeTone = "neutral" | "success" | "danger" | "info" | "warning";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

const TONE_STYLES: Record<BadgeTone, string> = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  danger: "bg-rose-50 text-rose-700 border-rose-200",
  info: "bg-sky-50 text-sky-700 border-sky-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
};

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone = "neutral", ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium",
        TONE_STYLES[tone],
        className,
      )}
      {...props}
    />
  ),
);
Badge.displayName = "Badge";
